// Plays a muted social clip while it is on screen, with tap-to-pause and a sound toggle.
// Reduced-motion visitors get no autoplay; they start clips with the play button.
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SSocialVideo extends HTMLElement {
  #ac;
  #observer;
  #userPaused = false;
  #retryArmed = false;
  #inView = false;

  connectedCallback() {
    this.video = this.querySelector('[data-social-video]');
    if (!this.video) return;
    this.playButton = this.querySelector('[data-social-play]');
    this.playIcon = this.querySelector('[data-social-play-icon]');
    this.soundButton = this.querySelector('[data-social-sound]');
    // Click-to-play clips never autoplay; pressing play starts them with sound.
    this.clickToPlay = this.hasAttribute('data-click-to-play');
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.playButton?.addEventListener('click', () => this.#togglePlay(), { signal });
    this.soundButton?.addEventListener('click', () => this.#toggleSound(), { signal });
    this.video.addEventListener('play', () => this.#syncPlay(), { signal });
    this.video.addEventListener('pause', () => this.#syncPlay(), { signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#inView = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        if (this.#inView) {
          if (!this.clickToPlay && !reduceMotion.matches && !this.#userPaused) this.#play();
        } else if (!this.video.paused) {
          this.video.pause();
        }
      }
    }, { threshold: [0, 0.5] });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #play() {
    const attempt = this.video.play();
    if (attempt) attempt.catch(() => this.#retryOnGesture());
  }

  // Phones in battery-saver modes refuse muted autoplay until the shopper touches the page.
  // Retry on the first tap, if the clip is still on screen and autoplay applies.
  #retryOnGesture() {
    if (this.clickToPlay || this.#retryArmed) return;
    this.#retryArmed = true;
    const retry = () => {
      this.#retryArmed = false;
      ['touchend', 'pointerup', 'click'].forEach((type) => document.removeEventListener(type, retry, true));
      if (!this.#inView || this.#userPaused || reduceMotion.matches || !this.video.paused) return;
      const attempt = this.video.play();
      if (attempt) attempt.catch(() => {});
    };
    ['touchend', 'pointerup', 'click'].forEach((type) =>
      document.addEventListener(type, retry, { capture: true, passive: true, once: true, signal: this.#ac.signal })
    );
  }

  #togglePlay() {
    if (this.video.paused) {
      if (this.clickToPlay && this.video.muted) this.#toggleSound();
      this.#userPaused = false;
      this.#play();
    } else {
      this.#userPaused = true;
      this.video.pause();
    }
  }

  #toggleSound() {
    const muted = !this.video.muted;
    this.video.muted = muted;
    if (!muted) {
      // Only one clip plays sound at a time.
      document.querySelectorAll('s-social-video').forEach((other) => {
        if (other !== this && other.video && !other.video.muted) other.mute();
      });
      if (this.video.paused) {
        this.#userPaused = false;
        this.#play();
      }
    }
    this.#syncSound();
  }

  mute() {
    this.video.muted = true;
    this.#syncSound();
  }

  #syncPlay() {
    const playing = !this.video.paused;
    if (this.playButton) {
      this.playButton.setAttribute('aria-label', playing ? this.playButton.dataset.pauseLabel : this.playButton.dataset.playLabel);
    }
    this.playIcon?.classList.toggle('opacity-0', playing);
  }

  #syncSound() {
    if (!this.soundButton) return;
    const soundOn = !this.video.muted;
    this.soundButton.setAttribute('aria-label', soundOn ? this.soundButton.dataset.muteLabel : this.soundButton.dataset.unmuteLabel);
    this.soundButton.querySelector('[data-icon-muted]')?.classList.toggle('hidden', soundOn);
    this.soundButton.querySelector('[data-icon-sound]')?.classList.toggle('hidden', !soundOn);
  }
}

if (!customElements.get('s-social-video')) customElements.define('s-social-video', SSocialVideo);
